import { useEffect, useState, useCallback, useRef } from 'react';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import AddWatermarkElementForm from '../add-watermark-element-form';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { deleteWatermarkSet, clearCurrentWatermarkSet } from '@/store/slices/watermarks';
import { selectWatermarkSets, selectWatermarksLoading } from '@/store/slices/watermarks/selectors';
import { selectProjectChannel } from '@/store/slices/project/selectors';
import OptionalComponent from '@ui/optional-component/optional-component';
import SearchInput from '@ui/search-input/search-input';
import type { IWatermarkElement, IWatermarkSet } from '@/types/watermarks.types';
import { FALLBACK_VIDEO, makeDefaultElement } from '../../../../constants/watermark.constants';
import WatermarkLibraryListItem from './watermark-library-list-item';
import DeleteWatermarkModal from './delete-watermark-modal';
import useSaveWatermarkSet from '../hooks/use-save-watermark-set';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import toastTrigger from '@ui/toast/toast-trigger';
import VideoIframe from '@/components/video-embed/video-embed';
import WatermarkCss from '@/components/watermark/watermark-edit';
import { EToastTypes } from '@ui/toast/toast.types';

type ManageWatermarkModalProps = {
  open: boolean;
  onComplete: () => void;
  onSelect?: (watermark: IWatermarkSet) => void;
};

function ManageWatermarkModal({ open, onComplete, onSelect }: ManageWatermarkModalProps) {
  const dispatch = useAppDispatch();

  const watermarkList = useAppSelector(selectWatermarkSets) ?? [];
  const channel = useAppSelector(selectProjectChannel);
  const isLoading = useAppSelector(selectWatermarksLoading);

  const [name, setName] = useState('');
  const [elements, setElements] = useState<IWatermarkElement[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWatermark, setSelectedWatermark] = useState<IWatermarkSet | null>(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<IWatermarkSet | null>(null);

  const [videoLoaded, setVideoLoaded] = useState(false);

  // track which element UUIDs are saved (triggers re-render when changed)
  const [savedUuids, setSavedUuids] = useState(new Set<string>());

  // track the watermark set being edited (null when creating new)
  const editingWatermarkRef = useRef<IWatermarkSet | null>(null);

  useEffect(() => {
    if (open) {
      if (watermarkList.length === 0) {
        setName('');
        setElements([makeDefaultElement()]);
        setSavedUuids(new Set());
        editingWatermarkRef.current = null;
        setIsEditingMode(true);
      } else {
        setIsEditingMode(false);
      }
      setSelectedWatermark(null);
      setSearchQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { saveWatermarkSet } = useSaveWatermarkSet();

  const handleEdit = useCallback((watermark: IWatermarkSet) => {
    setName(watermark.name);
    setElements(watermark.elements ?? []);
    setIsDirty(false);
    setVideoLoaded(false);
    setSavedUuids(new Set(watermark.elements?.map((e) => e.uuid) ?? []));
    editingWatermarkRef.current = watermark;
    setIsEditingMode(true);
  }, []);

  const handleDelete = async () => {
    if (!confirmDelete || !channel) return;
    try {
      await dispatch(deleteWatermarkSet({ channel: channel as number, uuid: confirmDelete.uuid })).unwrap();
      setConfirmDelete(null);
    } catch {
      toastTrigger('Error deleting watermark', {
        type: EToastTypes.error,
      });
    }
  };

  const handleElementChange = useCallback((updated: IWatermarkElement) => {
    setElements((prev) => prev.map((e) => (e.uuid === updated.uuid ? updated : e)));
    setIsDirty(true);
  }, []);

  const handleElementAdd = () => {
    setElements((prev) => [...prev, makeDefaultElement()]);
    setIsDirty(true);
  };

  const handleElementRemove = useCallback((elementUuid: string) => {
    setElements((prev) => prev.filter((e) => e.uuid !== elementUuid));
  }, []);

  const handleElementDelete = useCallback((elementUuid: string) => {
    setElements((prev) => prev.filter((e) => e.uuid !== elementUuid));
    setIsDirty(true);
  }, []);

  // both save buttons call this — saves everything currently in state
  const handleSave = useCallback(async () => {
    const saved = await saveWatermarkSet(editingWatermarkRef.current, { name: name.trim(), elements });
    if (!saved) return;
    onSelect?.(saved);
    onComplete();
  }, [saveWatermarkSet, name, elements, onSelect, onComplete]);

  // element save: local draft only — collapses the element form without an API call
  // the real save happens when the user clicks Save on the full-screen modal
  const handleElementSave = useCallback(async () => {
    setSavedUuids(new Set(elements.map((e) => e.uuid)));
    setIsDirty(true);
  }, [elements]);

  const filteredWatermarkList = searchQuery.trim()
    ? watermarkList.filter((w) => w.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : watermarkList;

  const showLibrary = open && !isEditingMode;
  const showEditor = open && isEditingMode;

  return (
    <>
      {/* library list modal */}
      <Modal
        open={showLibrary}
        customSize={{ width: '640px', height: '562px' }}
        contentWrapperClassName="padding-h-40 padding-t-24 padding-b-16 overflow-hidden flex-1 flex-column"
        contentClassName="overflow-hidden flex-column"
        backgroundColor="var(--gray-900)"
        focusLockProps={{ autoFocus: false }}
        RenderHeaderContent={() => <Text className="H4">Watermarks</Text>}
        RenderFooterContent={() => (
          <Container className="flex ai-center jc-space-between">
            <Button
              onClick={() => {
                dispatch(clearCurrentWatermarkSet());
                setName('');
                setElements([makeDefaultElement()]);
                setIsDirty(false);
                setVideoLoaded(false);
                setSavedUuids(new Set());
                editingWatermarkRef.current = null;
                setIsEditingMode(true);
              }}
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
              className="L1 padding-v-16 padding-h-24"
            >
              Create New
            </Button>
            <Container background="var(--gray-900)" className="flex fd-row jc-flex-end ai-flex-end gap-16">
              <Button
                onClick={onComplete}
                backgroundColor="var(--gray-600)"
                hoverBackgroundColor="var(--gray-500)"
                className="L1 padding-v-16 padding-h-24"
              >
                Cancel
              </Button>
              <Button
                backgroundColor={getThemeColorCSSVariable()}
                hoverBackgroundColor={getThemeColorsCssMixVariable()}
                className="padding-v-16 padding-h-24"
                disabledOpacity={0.6}
                disabled={!selectedWatermark}
                onClick={() => {
                  if (selectedWatermark) {
                    onSelect?.(selectedWatermark);
                    onComplete();
                  }
                }}
              >
                <Text className="L1" color={getThemeColorContrastCSSVariable()}>
                  Select
                </Text>
              </Button>
            </Container>
          </Container>
        )}
      >
        <Container className="flex-column gap-8 padding-v-24 overflow-hidden flex-1" style={{ minHeight: 0 }}>
          <OptionalComponent display={watermarkList.length > 0}>
            <Container className="padding-b-8" style={{ flexShrink: 0 }}>
              <SearchInput onSubmit={setSearchQuery} debounceTime={300} onClear={() => setSearchQuery('')} />
            </Container>
          </OptionalComponent>
          <OptionalComponent display={filteredWatermarkList.length > 0}>
            <Container className="flex-column gap-8 overflow-y-auto" style={{ flex: '1 1 0' }}>
              {filteredWatermarkList.map((watermark: IWatermarkSet) => (
                <WatermarkLibraryListItem
                  key={watermark.uuid}
                  watermark={watermark}
                  isSelected={selectedWatermark?.uuid === watermark.uuid}
                  onSelect={setSelectedWatermark}
                  onEdit={handleEdit}
                  onDelete={setConfirmDelete}
                />
              ))}
            </Container>
          </OptionalComponent>
          <OptionalComponent display={filteredWatermarkList.length === 0}>
            <Container className="flex flex-1 ai-center jc-center padding-b-8">
              <Text className="H4" color="var(--global-ico-text-3)">
                {searchQuery.trim() ? 'No watermarks match your search' : 'There are no watermarks to show'}
              </Text>
            </Container>
          </OptionalComponent>
        </Container>
      </Modal>

      {/* full-screen editor modal */}
      <Modal
        open={showEditor}
        fadeTransition={false}
        transitionDuration={400}
        size="full"
        withZoom={false}
        backgroundColor="var(--gray-1000)"
        showBackdrop={false}
        delayContentRender={150}
      >
        <Container margin="auto" className="flex-column padding-v-56 gap-24" maxWidth="960px" height="100%">
          <InputWithOverlay
            inputHeight="38px"
            inputWidth="100%"
            border="1px solid transparent"
            hoverBorder="1px solid var(--gray-400)"
            typography="H2"
            design={undefined}
            inputProps={{
              autoComplete: 'off',
              name: 'watermark-name',
              className: 'H2 padding-2 radius-8',
              placeholder: 'Add a watermark name here',
              value: name,
              onChange: (e) => {
                setName(e.target.value);
                setIsDirty(true);
              },
            }}
          />

          <Container className="flex gap-32">
            <Container className="flex-column gap-10 flex-1" maxWidth="280px">
              <Text className="H6">Watermark</Text>

              <Container className="flex-column gap-8">
                {elements.map((el) => {
                  const isNew = !savedUuids.has(el.uuid);
                  return (
                    <AddWatermarkElementForm
                      key={el.uuid}
                      element={el}
                      initiallyEditing={isNew}
                      canCancel={!isNew || elements.length > 1}
                      canDelete={!isNew && elements.filter((e) => savedUuids.has(e.uuid)).length > 1}
                      onChange={handleElementChange}
                      onSave={handleElementSave}
                      onDelete={() => handleElementDelete(el.uuid)}
                      onRemove={() => handleElementRemove(el.uuid)}
                    />
                  );
                })}
              </Container>

              <Button
                design="LocalBttnSecHalfTransp"
                height="68px"
                backgroundColor="var(--global-cont-1)"
                borderRadius="8px"
                className="flex ai-center jc-center gap-10"
                disabled={elements.some((el) => !savedUuids.has(el.uuid))}
                disabledOpacity={0.4}
                onClick={handleElementAdd}
              >
                <Icon color={getThemeColorCSSVariable()} name={ICONS.ADD} size={12} />
                <Text className="S2" color={getThemeColorCSSVariable()}>
                  Add Element
                </Text>
              </Button>
            </Container>

            <Container className="flex-column gap-10 flex-1">
              <Text className="H6">Preview</Text>
              <Container borderRadius="16px" className="flex-1 position-relative">
                <Container
                  height={365 * 2 + 'px'}
                  width={648 * 2 + 'px'}
                  className="position-absolute top-0 left-0"
                  style={{ transform: 'scale(0.5)', transformOrigin: 'top left' }}
                >
                  <VideoIframe
                    height={365 * 2}
                    width={648 * 2}
                    fallbackVideo={FALLBACK_VIDEO}
                    style={{ borderRadius: 16 }}
                    onLoad={() => setVideoLoaded(true)}
                  />
                </Container>
                {videoLoaded && elements.length > 0 && (
                  <Container height="365px" width="648px" className="position-absolute top-0 left-0">
                    <WatermarkCss
                      watermark={{ ...editingWatermarkRef.current, name, elements } as IWatermarkSet}
                      onElementChange={handleElementChange}
                    />
                  </Container>
                )}
              </Container>
            </Container>
          </Container>

          <Container className="flex flex-1 ai-flex-end jc-flex-end gap-16">
            <Button
              design="LocalBttnSec"
              designSize="medium"
              onClick={() => {
                if (watermarkList.length > 0) setIsEditingMode(false);
                else onComplete();
              }}
              disabled={isLoading}
              disabledOpacity={0.6}
            >
              <Text className="L1">Cancel</Text>
            </Button>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              designSize="medium"
              className="flex ai-center jc-center"
              onClick={handleSave}
              disabled={name.trim() === '' || isLoading || (editingWatermarkRef.current !== null && !isDirty)}
              disabledOpacity={0.6}
            >
              <Text className="L1" color={getThemeColorContrastCSSVariable()}>
                Save
              </Text>
            </Button>
          </Container>
        </Container>
      </Modal>

      <DeleteWatermarkModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onDelete={handleDelete}
        working={isLoading}
      />
    </>
  );
}

export default ManageWatermarkModal;
